/**
 * Sharing a document with the client — FR-F4. A real kit `Dialog` since Wave
 * 15C (C14 §10.6 item 7); it was an inline card under the list before.
 *
 * ## The one-way door, and the gate in front of it
 *
 * `POST /api/v1/documents/{id}/share` sits behind `platform/stepup.py`: it wants
 * an `X-StepUp-Assertion` for this app's audience and refuses with
 * `STEPUP_REQUIRED` without one. That is correct and it is tested against the
 * real dependency.
 *
 * The assertion comes from `components/StepUpDialog`, over core-auth's own
 * `POST /stepup/otp/send` and `POST /stepup/challenge` (the Wave 4 close) — it
 * is used for exactly one request and never stored. **No PIN box or OTP field
 * is built here**: a challenge in this app would be a local re-implementation
 * of core's, which CLAUDE.md §7 and constitution §1 both forbid.
 *
 * **One dialog at a time.** While the step-up dialog is open this one steps
 * aside (it keeps its note), and comes back if the step-up is closed or the
 * share is refused — two stacked overlays would both answer Escape, and the
 * one underneath would take the step-up with it.
 *
 * **Try first, then challenge** — C27 (spec §10.19), ADR-0056 §7, UAT report
 * #16. *Share with client* sends the share; only a `STEPUP_REQUIRED` answer
 * opens the step-up, and the share is then sent once more with the assertion
 * (`useStepUp`, the pattern publishing always had). Before C27 the code was
 * asked for before anything was sent, whether the service wanted one or not.
 *
 * ## The copy is doing work
 *
 * *"This cannot be undone"* is above the button and not in a tooltip. Sharing is
 * irreversible because the client has already had the file, and a firm that
 * discovers that afterwards discovers it from the client.
 */

import { Alert, Button, Dialog, Textarea } from '@fiveox/ui'
import { Send } from 'lucide-react'
import { useCallback, useState } from 'react'

import { StepUpDialog, useStepUp } from '../../components/StepUpDialog'
import { ApiError } from '../../lib/api'
import { type DocumentRow, documents } from '../../lib/api/documents'
import { DialogActions, FORM_GRID, FormRow } from '../../components/page'

export function ShareDialog({
  document: row,
  onClose,
  onShared,
}: {
  document: DocumentRow
  onClose: () => void
  onShared: () => void
}) {
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Every refusal but the step-up's own. `STEPUP_INVALID` stays in the step-up
  // dialog, which asks for another code; a `CONFLICT` — the one-way door, hit
  // from a stale screen — carries the service's own words, which already say
  // what happened.
  const refused = useCallback((err: unknown) => {
    setError(err instanceof ApiError ? err.message : 'The document could not be shared.')
  }, [])
  const stepUp = useStepUp(refused)

  /** The gated action. No assertion on the first try; `''` sends no header. */
  const share = async (assertion?: string) => {
    await documents.share(row.id, assertion ?? '', note || undefined)
    onShared()
  }

  const start = async () => {
    setBusy(true)
    setError(null)
    try {
      await stepUp.attempt(share)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Dialog
        open={!stepUp.challenging}
        onClose={onClose}
        title={`Share “${row.title || row.filename}” with your client`}
        error={error}
        width="lg"
      >
        <p className="text-muted-foreground mb-4 text-sm">
          They will be able to open it from their portal.
        </p>

        {/* Above the button, not in a tooltip. */}
        <Alert tone="warning">
          <strong>This cannot be undone.</strong> Once shared, your client has the document. To
          correct it, upload a new version and share that instead.
        </Alert>

        <div className={`${FORM_GRID} mt-5`}>
          <FormRow label="A note for your client" htmlFor="share-note" optional>
            <Textarea
              id="share-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Here is the computation for this year."
              rows={3}
            />
          </FormRow>
        </div>

        <DialogActions>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void start()} loading={busy} loadingText="Sharing…">
            <Send size={16} aria-hidden /> Share with client
          </Button>
        </DialogActions>
      </Dialog>

      <StepUpDialog
        {...stepUp.dialog}
        title="Share this document"
        what={
          'Sharing cannot be undone — the client has the document from the moment you confirm, ' +
          'and there is no un-share. To correct it afterwards you upload a new version and share that.'
        }
      />
    </>
  )
}
